<!-- eslint-disable vue/no-v-html -->
<template>
  <div ref="overviewSection" class="overview-media-items">
    <div
      v-for="(item, index) in media"
      :key="`media-${index}`"
      class="overview-media-item-wrapper"
    >
      <div v-if="item.embed_code || item.image" class="media-wrapper">
        <div
          v-if="item.embed_code"
          class="story-inline-video"
        >
          <div
            v-html="toggleEmbed ? item.embed_code : (item.lazy_embed_code || '' )"
          />
        </div>
        <div
          v-else
          class="story-img-container open-carousel-modal-on-click"
          :data-story-image-id="item.slug"
        >
          <VLazyImage
            class="story-inline-image"
            :alt="item.alt_text || 'image'"
            :srcset="`${item.image}?width=450 450w,
              ${item.image}?width=595&height=350 595w,
              ${item.image} 850w`"
            :src="item.image"
            :src-placeholder="placeholderImg"
            sizes="(max-width: 450px) 450px, (max-width: 595px) 595px, 850px"
            width="850"
            height="500"
          />
          <font-awesome-icon
            class="hover-mag-glass"
            icon="search-plus"
          />
        </div>
        <div
          v-if="item.description"
          class="media-description"
          :class="{ 'hide-captions-mobile-auth' : isAuthenticated }"
        >
          {{ item.description }}
        </div>
      </div>
      <div class="content-wrapper">
        <div
          v-if="item.html_body"
          class="pdp-deal-body pdp-description"
          :class="{ 'hide-captions-mobile-auth' : isAuthenticated }"
          v-html="item.html_body"
        />
        <div
          v-else
          class="content-container"
          v-html="item.content"
        />
      </div>
    </div>
  </div>
</template>

<script>
import frag from 'vue-frag';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import VLazyImage from 'v-lazy-image';
import { PLACEHOLDER_IMG } from '@/constants/global';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const DEAL_COPY_DELIMITER = 'START_EXP';
const VARIANT_DELIMITER = 'VARIANT_SPLIT';

export default {
  directives: { frag },
  components: {
    FontAwesomeIcon,
    VLazyImage,
  },
  props: {
    slug: {
      type: String,
      default: '',
    },
    media: {
      type: Array,
      default: () => [],
    },
    header: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      placeholderImg: PLACEHOLDER_IMG,
      toggleEmbed: false,
    };
  },
  async fetch() {
    // look for experimental html_body (goal: test deal copy variants)
    //
    // experiment name will take form: {DEAL_SLUG}-deal-copy
    // variants: a, b, c, ...
    //
    // in deal story > html_body, add 'START_EXP' delimiter to beginning of html_body (make sure to
    // use html editor so you're not accidentally adding extraneous <p> or similar tags)
    //
    // before each add'l variant, add 'VARIANT_SPLIT' delimiter to signify start of new variant
    // here's an example of what html_body might look like:
    //
    // START_EXP<h1> variant 1 </h1>VARIANT_SPLIT<h2> variant 2</h2>

    const mediaItem = this.media.find((item) => item.html_body?.startsWith(DEAL_COPY_DELIMITER));

    if (mediaItem && this.slug) {
      // remove initial delimiter & extract each deal copy variant
      const body = mediaItem.html_body.slice(DEAL_COPY_DELIMITER.length);
      const variants = body.split(VARIANT_DELIMITER);

      if (variants?.length > 1) {
        const DEAL_COPY_EXPERIMENT_NAME = `${this.slug}-deal-copy`;

        await this.$store.dispatch('experiments/enrollByName', DEAL_COPY_EXPERIMENT_NAME);
        const variant = this.$nuxt.$store.getters['experiments/getExperimentVariant'](
          DEAL_COPY_EXPERIMENT_NAME,
        );
        const variantIndex = variant?.charCodeAt(0) - 'a'.charCodeAt(0);

        if (variantIndex >= 0 && variantIndex < variants.length) {
          mediaItem.html_body = variants[variantIndex];
        } else {
          // eslint-disable-next-line prefer-destructuring
          mediaItem.html_body = variants[0]; // let's give user default in case of issue
        }
      }
    }
  },
  computed: {
    isAuthenticated() {
      return !!this.$store.state?.session?.user;
    },
  },
  mounted() {
    this.handleDebouncedScroll = debounce(this.onScroll, 100);
    window.addEventListener('scroll', this.handleDebouncedScroll);

    const clickableStoryImages = document.querySelectorAll('.open-carousel-modal-on-click');
    clickableStoryImages.forEach((imgElement) => imgElement.addEventListener('click', this.openCarouselModal));
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleDebouncedScroll);

    const clickableStoryImages = document.querySelectorAll('.open-carousel-modal-on-click');
    clickableStoryImages.forEach((imgElement) => imgElement.removeEventListener('click', this.openCarouselModal));
  },
  methods: {
    // On scroll into view, load embed video
    onScroll() {
      if (this.toggleEmbed) return;

      const el = get(this, ['$refs', 'overviewSection']);

      if (el) {
        const rect = el.getBoundingClientRect();
        const isVisible = (
          (rect.top >= 0 && rect.top <= window.innerHeight)
          || (rect.bottom >= 0 && rect.bottom <= window.innerHeight)
        );

        if (isVisible) this.toggleEmbed = true;
      }
    },
    openCarouselModal(event) {
      if (event.currentTarget?.dataset?.storyImageId) {
        this.$store.dispatch(
          'carouselModal/openModalToImage',
          { targetImageSlug: event.currentTarget.dataset.storyImageId },
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
  /* TODO -- Remove once this is a global style */
  @import '~/styles/dorado/main';
  @import '~/styles/utils/breakpoints';

  .content-container::v-deep {
    font-size: 16px; // default font-size if html tags missing in admin

    h1,
    h2,
    h3,
    h4,
    h5 {
      font-family: $headings-font-family;
      font-weight: $headings-font-weight;
    }

    p,
    a,
    span {
      font-weight: $font-weight-normal;
      font-size: $font-size-base;
      font-family: $font-family-base;
      line-height: 24px;
    }

    b {
      font-weight: $font-weight-bold;
    }
  }

  .media-description {
    color: #434d59;
    font-style: italic;
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    padding-top: 8px;
    text-align: center;
    width: 100%;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .row {
    margin-left: 0;
    margin-right: 0;
    margin-top: 64px;

    &:first-child {
      margin-top: 0;
    }
  }

  .hover-mag-glass,
  .pdp-deal-body ::v-deep .hover-mag-glass {
    font-size: 32px;
    transition: 0.5s ease;
    opacity: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    text-align: center;
  }

  .story-inline-image,
  .pdp-deal-body ::v-deep .story-inline-image {
    opacity: 1;
    display: block;
    width: 100%;
    height: auto;
    transition: 0.5s ease;
    backface-visibility: hidden;
    border: 1px solid #c7ced7;
    border-radius: 4px;
    box-sizing: border-box;
    margin: 0 auto;
  }

  .pdp-deal-body::v-deep {
    .story-img-container {
      position: relative;
    }

    img {
      border-radius: 4px;
      max-width: 100%;
      height: auto;
      border: 1px solid #c7ced7;
      margin-bottom: 8px;
    }

    b {
      font-weight: $font-weight-bold;
    }

    ul,
    ol {
      margin-bottom: 2.5rem;
    }
  }

  /*! purgecss start ignore */

  @include media-breakpoint-down(md) {
    .media-description.hide-captions-mobile-auth,
    .hide-captions-mobile-auth ::v-deep h5 i {
      display: none;
    }
  }

  /*! purgecss end ignore */

  .overview-media-item-wrapper {
    margin-bottom: 3rem;

    &:last-child {
      margin-bottom: 2rem;
    }
  }

  .media-wrapper + .content-wrapper {
    margin-top: 24px;
  }

  /* order not part of bootstrap for some reason */
  .order-1 {
    order: 1;
  }

  .order-2 {
    order: 2;
  }

  @media (min-width: 768px) {
    .order-md-1 {
      order: 1;
    }

    .pdp-deal-body::v-deep .story-img-container:hover {
      cursor: pointer;

      .story-inline-image {
        opacity: 0.2;
      }

      .hover-mag-glass {
        opacity: 1;
      }
    }
  }

  @media (max-width: 767px) {
    .row {
      margin-bottom: 48px;
    }
  }

  @media only screen and (min-width: 767px) {
    .show-mobile {
      display: none;
    }
  }

  @media only screen and (max-width: 767px) {
    .show-lg {
      display: none;
    }
  }

  /**
  * Override legacy styles from `project.css`. This should be removed/updated
  * once dorado is implemented.
  *
  * Using this for now, when `project.css` is removed, should utilize
  *   @supports and aspect-ratio
  */
  .story-inline-video,
  .overview-media-item-wrapper::v-deep .story-inline-video {
    border-radius: 0;
    margin: 0;
    max-width: 100%;
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
</style>
